import { useData } from "@hooks/useData";
import http from "@core/fetch";
import { components } from "@apiSchema";
import { ID } from "@models";
import { capitalize } from "@datassential/platform-ui-lib";
import { BarChartItem } from "@components/BarChart/BarChart";

type ApiMenuChart = components["schemas"]["SnapCategoryProfileMenuModel"];
type ApiMenuKeyword = components["schemas"]["SnapMenuKeyword"];

export type CategoryProfileRegionalSkew = components["schemas"]["SnapRegionalSkew"];

export interface CategoryProfileMenuChart {
    instantChartId: number;
    instantChartName: string;
    subCategoryName: string;
    topByPenetration: BarChartItem[];
    topByGrowth: BarChartItem[];
    regionalSkews: CategoryProfileRegionalSkew[];
}

function toChartItems(
    keywords: ApiMenuKeyword[],
    metric: "penetration" | "fourYearGrowth",
): BarChartItem[] {
    return (keywords ?? [])
        .flatMap((keyword, index) => {
            const value = keyword[metric];
            return typeof value === "number"
                ? [{ name: capitalize(keyword.wordName ?? ""), value, id: `${keyword.wordId ?? 0}_${index}` }]
                : [];
        })
        .sort((a, b) => b.value - a.value)
}

async function getCategoryProfileMenu(id: ID): Promise<CategoryProfileMenuChart[]> {
    const result: ApiMenuChart[] = await http.getXHR("get category profile menu", { id });

    return (result ?? []).flatMap(chart =>
        chart.instantChartName
            ? [{
                instantChartId: chart.instantChartId ?? 0,
                instantChartName: chart.instantChartName,
                subCategoryName: chart.subCategoryName ?? "",
                topByPenetration: toChartItems(chart.topByPenetration ?? [], "penetration"),
                topByGrowth: toChartItems(chart.topByGrowth ?? [], "fourYearGrowth"),
                regionalSkews: chart.regionalSkews ?? [],
            }]
            : [],
    );
}

export default function useCategoryProfileMenu(id: ID) {
    return useData<CategoryProfileMenuChart[]>([], getCategoryProfileMenu, id);
}
